import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = '/api';

const CrudManager = ({ entityType, title, columns, formFields }) => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Загрузка данных
  const loadItems = async () => {
    try {
      const response = await axios.get(`${API_URL}/${entityType}`);
      setItems(response.data);
    } catch (err) {
      setError('Ошибка загрузки: ' + err.message);
    }
  };

  useEffect(() => {
    loadItems();
  }, [entityType]);

  // Открытие формы (создание/редактирование)
  const handleOpenForm = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ ...item });
    } else {
      setEditingItem(null);
      setFormData({});
    }
    setShowForm(true);
    setError('');
  };

  // Отмена формы
  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData({});
  };

  // Изменение поля формы
  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Сохранение (создание или обновление)
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      if (editingItem) {
        // Обновление
        await axios.put(`${API_URL}/${entityType}/${editingItem.id}`, formData);
      } else {
        // Создание
        await axios.post(`${API_URL}/${entityType}`, formData);
      }
      await loadItems();
      handleCancel();
    } catch (err) {
      setError('Ошибка сохранения: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Удаление
  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены?')) return;

    try {
      await axios.delete(`${API_URL}/${entityType}/${id}`);
      await loadItems();
    } catch (err) {
      setError('Ошибка удаления: ' + err.message);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2>{title}</h2>
        <button onClick={() => handleOpenForm()} style={styles.btnCreate}>
          ➕ Добавить
        </button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {/* Таблица данных */}
      <table style={styles.table}>
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th key={idx} style={styles.th}>{col.header}</th>
            ))}
            <th style={styles.th}>Действия</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} style={styles.empty}>
                Нет данных. Нажмите "Добавить"
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <tr key={item.id}>
                {columns.map((col, idx) => (
                  <td key={idx} style={styles.td}>
                    {col.render ? col.render(item) : item[col.field]}
                  </td>
                ))}
                <td style={styles.td}>
                  <button 
                    onClick={() => handleOpenForm(item)} 
                    style={styles.btnEdit}
                  >
                    ✏️
                  </button>
                  <button 
                    onClick={() => handleDelete(item.id)} 
                    style={styles.btnDelete}
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Форма создания/редактирования */}
      {showForm && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3>{editingItem ? 'Редактирование' : 'Создание'}</h3>
            
            {formFields.map((field, idx) => (
              <div key={idx} style={styles.formGroup}>
                <label style={styles.label}>{field.label}</label>
                {field.type === 'select' ? (
                  <select
                    value={formData[field.name] || ''}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    style={styles.input}
                  >
                    <option value="">Выберите...</option>
                    {field.options.map((opt, i) => (
                      <option key={i} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type || 'text'}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    style={styles.input}
                    placeholder={field.placeholder}
                  />
                )}
              </div>
            ))}

            <div style={styles.modalButtons}>
              <button onClick={handleSave} disabled={loading} style={styles.btnSave}>
                {loading ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button onClick={handleCancel} style={styles.btnCancel}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    background: '#fff',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  btnCreate: {
    padding: '10px 20px',
    background: '#075A64',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  errorBox: {
    background: '#ffebee',
    color: '#c62828',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '15px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    background: '#075A64',
    color: '#fff',
    fontWeight: 'bold',
  },
  td: {
    padding: '10px',
    borderBottom: '1px solid #ddd',
  },
  empty: {
    padding: '40px',
    textAlign: 'center',
    color: '#999',
  },
  btnEdit: {
    padding: '5px 10px',
    background: '#0C7281',
    color: '#fff',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    marginRight: '5px',
  },
  btnDelete: {
    padding: '5px 10px',
    background: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: '#fff',
    padding: '30px',
    borderRadius: '10px',
    width: '500px',
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  formGroup: {
    marginBottom: '15px',
  },
  label: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '5px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  modalButtons: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px',
  },
  btnSave: {
    flex: 1,
    padding: '12px',
    background: '#075A64',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  btnCancel: {
    flex: 1,
    padding: '12px',
    background: '#ccc',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
};

export default CrudManager;
